import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FileText } from "lucide-react";
import { ChatPanel } from "@/components/ui/ChatPanel";
import { useDocumentChat } from "./hooks/use-document-chat";
import { ChatMessageBubble } from "./chat/ChatMessageBubble";
import { ChatEmptyState } from "./chat/ChatEmptyState";
import { ChatStreamingIndicator } from "./chat/ChatStreamingIndicator";
import { ChatThreadHeader } from "./chat/ChatThreadHeader";
import { useTranslation } from "@audit-app/utils";
import type { DocumentView } from "./types";
import type { Id } from "@/packages/types";

interface DocumentDetailChatProps {
  document: DocumentView;
}

export function DocumentDetailChat({ document }: DocumentDetailChatProps) {
  const { t } = useTranslation(["documents"]);
  const docId = document.id as Id<"documents">;
  const isProcessing =
    document.aiSummary?.status === "generating" ||
    document.status === "under-review";

  const {
    messages,
    sendMessage,
    isStreaming,
    isSending,
    isLoading,
    threads,
    threadId,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
  } = useDocumentChat(docId);

  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on every render (matches ChatPanelMessages pattern)
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  const hasMessages = messages.length > 0;

  const handleQuickAction = (text: string) => {
    sendMessage(text);
  };

  return (
    <ChatPanel inline>
      {/* Thread selector header — shown when threads exist */}
      {threads.length > 0 ? (
        <ChatThreadHeader
          threads={threads}
          activeThreadId={threadId}
          onNewThread={startNewThread}
          onSelectThread={selectThread}
          onDeleteThread={deleteThread}
          onRenameThread={renameThread}
        />
      ) : null}

      {/* Messages area */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            className="flex items-center gap-2 text-muted"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="h-4 w-4 border-2 border-muted/30 border-t-muted rounded-full animate-spin" />
            <span className="text-sm">
              {t("documents:documentDetailChat.loadingConversation")}
            </span>
          </motion.div>
        </div>
      ) : !hasMessages ? (
        <div className="flex-1 overflow-y-auto">
          <ChatEmptyState
            isProcessing={isProcessing}
            onQuickAction={handleQuickAction}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((message, i) => (
            <ChatMessageBubble key={message.id} message={message} index={i} />
          ))}
          <AnimatePresence>
            {isStreaming && <ChatStreamingIndicator />}
          </AnimatePresence>
          <div ref={bottomRef} />
        </div>
      )}

      {/* Input bar */}
      <ChatPanel.Input
        placeholder={
          isProcessing
            ? t("documents:documentDetailChat.documentProcessing")
            : isSending
              ? t("documents:documentDetailChat.sending")
              : t("documents:documentDetailChat.askAboutDocument")
        }
        onSend={sendMessage}
        context={{
          label: document.title,
          icon: FileText,
        }}
      />
    </ChatPanel>
  );
}
